/**
 * Target Company picker for the Data Import upload dialog.
 *
 * Extracted from DataImport.tsx. Company groups are offered first, then
 * individual sites. A user who can reach a single target gets a read-only
 * line instead of a one-option dropdown — and no "whole group" option, which
 * would otherwise write to sites outside their access.
 *
 * CR-8256: the Users import (`customerOnly`) offers customer companies only
 * and never a whole group — new accounts are created in the chosen customer
 * and the server refuses anything else.
 */
import type { Site } from '../../lib/api';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import { AlertTriangle, Building2 } from 'lucide-react';

/**
 * An accessible site as `/core/sites/accessible/` returns it. SiteSerializer
 * includes the company type (Site IS Company); the shared `Site` type does
 * not declare it, so it is added here rather than growing that file.
 */
export type TargetSite = Site & { company_type?: string };

/** A company group offered as a "(whole group)" target. */
export interface TargetGroup {
  id: number;
  name: string;
}

interface UploadTargetSelectProps {
  /** Every company the user may import into (accessible sites). */
  sites: TargetSite[];
  /** Distinct groups across `sites`; offered as "(whole group)" options. */
  availableGroups: TargetGroup[];
  /** Selected target: a site id, or `groupPrefix` + group id; '' for none. */
  value: string;
  /** Called with the new target value. */
  onChange: (value: string) => void;
  /** The user can reach exactly one site, so there is nothing to choose. */
  onlyOneTarget: boolean;
  /** Rows land on the target's group (Commodities, Fault Codes). */
  groupOwned?: boolean;
  /** Customer companies only and no group options (Users import). */
  customerOnly?: boolean;
  /** Prefix marking a value as a company group rather than a site. */
  groupPrefix: string;
}

/**
 * The sites a customer-only import (Users) may target.
 *
 * @param sites - accessible sites from `/core/sites/accessible/`
 * @returns only the sites whose company is a customer
 */
export function customerTargetSites<T extends TargetSite>(sites: T[]): T[] {
  return sites.filter(s => s.company_type === 'customer');
}

/**
 * Renders the Target Company field of the upload dialog.
 *
 * @param props - see {@link UploadTargetSelectProps}
 * @returns the label plus a dropdown, a read-only line, or a notice when
 *   nothing can be targeted
 */
export default function UploadTargetSelect({
  sites, availableGroups, value, onChange, onlyOneTarget, groupOwned, customerOnly, groupPrefix,
}: UploadTargetSelectProps) {
  const options = customerOnly ? customerTargetSites(sites) : sites;
  const groups = customerOnly ? [] : availableGroups;
  const readOnly = customerOnly ? options.length === 1 : onlyOneTarget;

  const selected = sites.find(s => String(s.id) === value);
  let helper = 'This data will be imported for the selected company';
  if (customerOnly) {
    helper = 'New users are created in this customer; sites in the file must belong to its group.';
  } else if (value.startsWith(groupPrefix)) {
    helper = 'This data will be shared by every site in the selected group';
  } else if (groupOwned && selected?.group_id && selected.group_name) {
    helper = `This data is stored once for ${selected.group_name} — every site in the group will share it`;
  }

  if (options.length === 0) {
    if (!customerOnly) return null;
    return (
      <div className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
        <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
        <span>You don't have access to a customer company to import users into.</span>
      </div>
    );
  }

  return (
    <div className="space-y-1.5">
      <label className="text-sm font-medium">
        Target Company{!readOnly && <span className="text-red-500"> *</span>}
      </label>
      {(!readOnly || customerOnly) && (
        <p className="text-xs text-muted-foreground">{helper}</p>
      )}
      {readOnly ? (
        <div className="flex items-center gap-2 rounded-md border bg-muted/30 px-3 py-2">
          <Building2 className="h-4 w-4 text-muted-foreground shrink-0" />
          <span className="text-sm font-medium">{options[0].name}</span>
        </div>
      ) : (
        <Select value={value} onValueChange={onChange}>
          <SelectTrigger>
            <SelectValue placeholder={customerOnly ? 'Select customer...' : 'Select company...'} />
          </SelectTrigger>
          <SelectContent>
            {groups.map(g => (
              <SelectItem key={`g${g.id}`} value={`${groupPrefix}${g.id}`}>
                {g.name} (whole group)
              </SelectItem>
            ))}
            {options.map(site => (
              <SelectItem key={site.id} value={String(site.id)}>{site.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}
    </div>
  );
}
